/**
 * ALOHA — navigation du menu mobile par panneaux.
 * Le panneau racine reste affiché ; ouvrir une entrée parente fait glisser
 * un second panneau par-dessus, avec un retour explicite.
 */
class AlohaDrawerNav extends HTMLElement {
  #observer = undefined;

  connectedCallback() {
    this.root = this.querySelector('[data-panel="root"]');
    if (!this.root) return;

    this.stack = [];
    this.addEventListener('click', this.#handleClick);

    // Revenir à la racine quand le tiroir se referme, sinon il se rouvre
    // sur le dernier sous-menu consulté.
    const overlay = this.closest('[data-ref="mobile-menu-overlay"]');
    if (overlay) {
      this.#observer = new MutationObserver(() => {
        if (!overlay.classList.contains('is-open')) this.reset();
      });
      this.#observer.observe(overlay, { attributes: true, attributeFilter: ['class'] });
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #handleClick = (event) => {
    const opener = event.target.closest('[data-panel-open]');
    if (opener && this.contains(opener)) {
      event.preventDefault();
      this.open(opener.getAttribute('data-panel-open'), opener);
      return;
    }

    const back = event.target.closest('[data-panel-back]');
    if (back && this.contains(back)) {
      event.preventDefault();
      this.back();
    }
  };

  #panel(id) {
    return this.querySelector(`[data-panel="${CSS.escape(id)}"]`);
  }

  #setState(panel, state) {
    panel.classList.toggle('aloha-drawer__panel--active', state === 'active');
    panel.classList.toggle('aloha-drawer__panel--parked', state === 'parked');
    const hidden = state !== 'active';
    panel.toggleAttribute('inert', hidden);
    panel.setAttribute('aria-hidden', String(hidden));
  }

  open(id, opener) {
    const next = this.#panel(id);
    const current = this.stack.length ? this.#panel(this.stack.at(-1).id) : this.root;
    if (!next || !current) return;

    this.#setState(current, 'parked');
    this.#setState(next, 'active');
    this.stack.push({ id, opener });
    next.scrollTop = 0;
    opener?.setAttribute('aria-expanded', 'true');
    next.querySelector('[data-panel-back]')?.focus();
  }

  back() {
    const last = this.stack.pop();
    if (!last) return;
    const leaving = this.#panel(last.id);
    const target = this.stack.length ? this.#panel(this.stack.at(-1).id) : this.root;
    if (leaving) this.#setState(leaving, 'stacked');
    if (target) this.#setState(target, 'active');
    last.opener?.setAttribute('aria-expanded', 'false');
    last.opener?.focus();
  }

  reset() {
    while (this.stack.length) {
      const { id, opener } = this.stack.pop();
      const panel = this.#panel(id);
      if (panel) this.#setState(panel, 'stacked');
      opener?.setAttribute('aria-expanded', 'false');
    }
    this.#setState(this.root, 'active');
  }
}

if (!customElements.get('aloha-drawer-nav')) {
  customElements.define('aloha-drawer-nav', AlohaDrawerNav);
}
